import { useCallback, useRef } from 'react'

/**
 * Single-click guard that works for both sync and async handlers.
 * - Unlocks on the next microtask (so rapid Play→Pause isn't eaten).
 * - Optional fallback delay keeps protection for slower ops (Next/Regenerate).
 */
export const useSingleClick = (delay = 600) => {
  const lockedRef = useRef(false)

  return useCallback(
    (fn: () => void | Promise<unknown>) => {
      if (lockedRef.current) return
      lockedRef.current = true
      try {
        const maybePromise = fn()
        Promise.resolve(maybePromise).finally(() => {
          // microtask unlock prevents swallowing immediate follow-up clicks
          queueMicrotask(() => {
            lockedRef.current = false
          })
        })
      } catch {
        queueMicrotask(() => {
          lockedRef.current = false
        })
      }

      // Optional fallback unlock in case a handler never resolves
      if (delay > 0) {
        window.setTimeout(() => {
          lockedRef.current = false
        }, delay)
      }
    },
    [delay],
  )
}
